import { useHistory } from "react-router-dom";
import { MutationOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { apisiseci } from "../../api";
import { Totem } from "../../../@types/totem";
import { AxErrorResponse } from "../../../@types/axios-error-response";

export type TotemUpdateData = Pick<
    Totem,
    | "descricao"
    | "cor_fonte"
    | "cor_fundo"
    | "token"
    | "senha_reset"
> & {
    local_atendimento: string
    orgao: number
    servicos: Array<string>
    prioridades: Array<string>
    logo: File | string
    ativo?: boolean
};

export function useTotemUpdate(id: Totem["id"], options?: MutationOptions<Totem, AxiosError<AxErrorResponse>, TotemUpdateData, unknown>) {
    const history = useHistory();
    const queryClient = useQueryClient();
    const token = localStorage.getItem("gov_access_token_sso");

    const mutation = useMutation<
        Totem,
        AxiosError<AxErrorResponse>,
        TotemUpdateData
    >({
        mutationKey: ["totem", "update", id],
        mutationFn: async (data) => {
            if (typeof data.logo === "string") {
                delete data.logo;
            }

            const payload = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (value instanceof File) {
                    payload.append(key, value);
                } else if (Array.isArray(value)) {
                    if (value.length === 1) {
                        payload.append(key, String(value[0]) + ',');
                    } else {
                        payload.append(key, String(value));
                    }
                } else {
                    payload.append(key, String(value));
                }
            });
            const response = await apisiseci.put<Totem>(
                `/cms/totens/${id}/`, payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    }
                }
            );
            return response.data;
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ["totem", "list"]
                }),
                queryClient.invalidateQueries({
                    queryKey: ["totem", "details", id]
                }),
            ]);

            toast.success("Totem atualizado com sucesso!");
            history.goBack();
        },
        onError: (error) => {
            console.error("Error updating totem:", error);
            const data = error?.response?.data || {};

            Object.keys(data).forEach((key) => {
                if (Array.isArray(data[key]) && data[key].length > 0) {
                    toast.error(data[key][0]);
                } else if (typeof data[key] === "string") {
                    toast.error(data[key]);
                }
            });

            if (Object.keys(data).length === 0) {
                toast.error("Algo deu errado, tente novamente!");
            }
        },
        ...options,
    });

    return mutation;
}
